import { Component, OnInit, ViewChild } from '@angular/core';
import { DxFormComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { filter, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { TrainingTypeModel } from 'src/app/models/training/training-type.model';
import { TrainingGroupCreationAndUpdateModel } from 'src/app/models/training-group-creation-and-update.model';
import { TrainingGroupService } from 'src/app/services/training-group.service';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { LoadingService } from 'src/app/services/loading.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TimeService } from 'src/app/services/time.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationDialogData } from 'src/app/components/shared/confirmation-dialog/confirmation-dialog.component';
import { TableColumn } from 'src/app/models/table-column.model';
import { PersonSummaryModel } from 'src/app/models/person/person-summary.model';
import { PersonModel } from 'src/app/models/person/person.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-training-groups-details',
  templateUrl: './training-groups-details.component.html',
  styleUrl: './training-groups-details.component.scss',
  standalone: false
})
export class TrainingGroupsDetailsComponent implements OnInit {
  showDeleteGroupModal: boolean = false;
  modalDeleteGroupConfirmationOptions: ConfirmationDialogData | null = null;

  editingMode = false;
  currentTrainingGroup: TrainingGroupModel | null = null;
  trainingTypeModelIDD = TrainingTypeModel.IDD;
  titleAndDescFormData = {
    name: '',
    description: '',
  };
  generalFormData!: {
    trainingType: number | null;
    useDefaultQualificationTarget: boolean;
    qualificationTargetHours: number;
    qualificationTargetMinutes: number;
  };
  defaultSelectOptions = {
    showClearButton: true,
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
  } as DxSelectBoxTypes.Properties;
  trainingTypes: DxSelectBoxTypes.Properties = {
    ...this.defaultSelectOptions,
    dataSource: [
      {
        id: TrainingTypeModel.IDD,
        name: this.translateService.instant('COMMON.FIELDS.TRAINING-TYPES.IDD'),
      },
      {
        id: TrainingTypeModel.Compliance,
        name: this.translateService.instant(
          'COMMON.FIELDS.TRAINING-TYPES.COMPLIANCE'
        ),
      },
      {
        id: TrainingTypeModel.General,
        name: this.translateService.instant(
          'COMMON.FIELDS.TRAINING-TYPES.GENERAL'
        ),
      },
    ],
    placeholder: '',
  };

  @ViewChild('titleAndDescForm', { static: false })
  titleAndDescForm!: DxFormComponent;
  @ViewChild('generalForm', { static: false }) generalForm!: DxFormComponent;

  constructor(
    private trainingGroupService: TrainingGroupService,
    private loadingService: LoadingService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private pageStateService: PageStateService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map((params) => params['id']),
        filter((id) => !!id),
        tap((id) => {
          this.editingMode = id !== 'new';
        }),
        switchMap((id) => {
          return this.loadingService.load(
            this.editingMode
              ? this.trainingGroupService.getTrainingGroup(id)
              : of(null)
          );
        })
      )
      .subscribe((trainingGroup) => {
        this.currentTrainingGroup = trainingGroup;

        if (!trainingGroup) {
          this.generalFormData = {
            trainingType: null,
            useDefaultQualificationTarget: true,
            qualificationTargetHours: 0,
            qualificationTargetMinutes: 0,
          };
          return;
        }

        const qualificationTargetHours =
          TimeService.getHoursFromAbsoluteMinutes(
            trainingGroup.qualificationTargetIddTimeMinutes || 0
          );
        const qualificationTargetMinutes =
          TimeService.getMinutesFromAbsoluteMinutes(
            trainingGroup.qualificationTargetIddTimeMinutes || 0
          );

        this.titleAndDescFormData.name = trainingGroup.name;
        this.titleAndDescFormData.description = trainingGroup.description || '';
        this.generalFormData = {
          trainingType: trainingGroup.trainingType,
          useDefaultQualificationTarget: trainingGroup.useDefaultQualificationTarget,
          qualificationTargetHours,
          qualificationTargetMinutes,
        };
      });
  }

  handleTimePicker(value: { hours: number; minutes: number }) {
    this.generalFormData.qualificationTargetHours = value.hours;
    this.generalFormData.qualificationTargetMinutes = value.minutes;
  }

  submit() {
    const isTitleAndDescFormValid =
      this.titleAndDescForm.instance.validate().isValid;
    const isGeneralFormValid = this.generalForm.instance.validate().isValid;
    if (!isTitleAndDescFormValid || !isGeneralFormValid) {
      return;
    }

    let qualificationTargetIddTimeMinutes: number | null = null;
    const trainingType = this.generalFormData.trainingType;
    if (typeof trainingType !== 'number') return;
    if (trainingType === TrainingTypeModel.IDD) {
      qualificationTargetIddTimeMinutes =
        this.generalFormData.qualificationTargetHours * 60 +
        this.generalFormData.qualificationTargetMinutes;
    }

    const model: TrainingGroupCreationAndUpdateModel = {
      name: this.titleAndDescFormData.name,
      trainingType: trainingType,
      description: this.titleAndDescFormData.description,
      qualificationTargetIddTimeMinutes: qualificationTargetIddTimeMinutes,
      useDefaultQualificationTarget:
        this.generalFormData.useDefaultQualificationTarget,
    };

    if (this.editingMode && this.currentTrainingGroup) {
      const obs = this.trainingGroupService.updateTrainingGroup(
        this.currentTrainingGroup.id,
        model
      );
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success('Training group saved');
        this.navigateBack();
      });
    } else {
      const obs = this.trainingGroupService.addTrainingGroup(model);
      this.loadingService.load(obs).subscribe(() => {
        this.toastrService.success('Training group added');
        this.navigateBack();
      });
    }
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreTrainingGroupsOverview
    );

    this.router.navigate(['administration']);
  }

  openDeleteTrainingGroupConfirmationModal() {
    this.modalDeleteGroupConfirmationOptions = {
      hideUndo: true,
      submitButtonText: this.translateService.instant('COMMON.DELETE'),
      message: this.translateService.instant(
        'ADMINISTRATION.DELETE-GROUP-CONFIRMATION-DIALOG.TEXT'
      ),
      dismissButtonText: this.translateService.instant('COMMON.CANCEL'),
    };
    this.showDeleteGroupModal = true;
  }

  deleteTrainingGroup() {
    this.loadingService
      .load(this.trainingGroupService.deleteTrainingGroup(this.currentTrainingGroup!.id))
      .subscribe({
        next: () => {
          this.navigateBack();
        },
        error: () => {
          this.toastrService.error('Unknown error');
        },
      });
  }
}
